/**
 * Settings Section Route — /settings/:section
 * Renders the section component for the given section.
 * Invalid section values redirect to /settings.
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState, useEffect } from 'react'
import { z } from 'zod'
import { useHasPermission, useUserPermissions } from '@/hooks/usePermissions'
import { REPORTS_SECTIONS, SECURITY_SECTIONS, DATA_SECTIONS, CALENDAR_SECTIONS, SYSTEM_SECTIONS } from './settings-sections'
import { GeneralSettings } from '@/components/features/admin/GeneralSettings'
import { UserManagement } from '@/components/features/admin/UserManagement'
import { DropdownOptions } from '@/components/features/admin/DropdownOptions'
import { ApiKeyManagement } from '@/components/features/admin/ApiKeyManagement'
import { RolesAndPermissions } from '@/components/features/admin/RolesAndPermissions'
import { ChangelogManagement } from '@/components/features/admin/ChangelogManagement'
import { HolidayManagement } from '@/components/features/admin/HolidayManagement'
import { DayTypeManagement } from '@/components/features/admin/DayTypeManagement'
import { ScoreCardMetricsConfig } from '@/components/features/admin/ScoreCardMetricsConfig'
import { DataMaintenance } from '@/components/features/admin/DataMaintenance'
import { UtilitiesSettings } from '@/pages/admin/UtilitiesSettings'
import { NotificationSettings } from '@/components/features/NotificationSettings/NotificationSettings'
import { KPIForm } from '@/components/features/kpi/KPIForm'
import { KPIProgress } from '@/components/features/kpi/KPIProgress'
import { Card, CardHeader, CardBody } from '@/components/common/Card'
import { Button } from '@/components/common/Button'
import { Select } from '@/components/common/Select'
import { useKPIProgress, useTeamKPIProgress } from '@/hooks/useKPI'
import { exportKPIProgress } from '@/lib/utils/csv'
import { getCurrentYear, getAvailableYears } from '@/lib/utils/date'
import type { KPIPeriod } from '@/types/database.types'
import styles from '@/pages/Settings.module.css'

const VALID_SECTIONS = [
  'general',
  'kpi',
  'notifications',
  'users',
  'roles',
  'attributes',
  'apikeys',
  'changelog',
  'holidays',
  'daytypes',
  'metrics',
  'maintenance',
  'utilities',
] as const

const sectionSchema = z.enum(VALID_SECTIONS)

// Sections flagged adminOnly in the settings nav require `settings:manage`.
// Enforced here at the route level so they can't be reached by pasting the URL
// (the sidebar only *hides* them) (#436).
const ADMIN_ONLY_SECTIONS = new Set(
  [...REPORTS_SECTIONS, ...SECURITY_SECTIONS, ...DATA_SECTIONS, ...CALENDAR_SECTIONS, ...SYSTEM_SECTIONS]
    .filter((s) => s.adminOnly)
    .map((s) => s.id as string),
)

const sectionSearchSchema = z.object({
  id: z.string().optional(),
})

export const Route = createFileRoute('/_authenticated/settings/$section')({
  validateSearch: sectionSearchSchema,
  component: SettingsSectionRoute,
})

function SettingsSectionRoute() {
  const { section: rawSection } = Route.useParams()
  const navigate = useNavigate()

  const parseResult = sectionSchema.safeParse(rawSection)
  const isAdminOnlySection = parseResult.success && ADMIN_ONLY_SECTIONS.has(parseResult.data)

  // Route-level authorization for admin-only sections (#436): require
  // `settings:manage`. Without it, redirect away instead of rendering the admin
  // tool — direct-URL access to /settings/users, /roles, /apikeys, etc. is
  // blocked, not just hidden from the sidebar.
  const canManageSettings = useHasPermission('settings', 'manage')
  // The KPI section is viewable by anyone with settings access, but *setting*
  // targets requires kpi:set_targets — gate just that affordance (#436). The
  // sidebar already hides the KPI link without it, but the settings index
  // redirects non-admins here by default, so the section can still be reached.
  const canSetKpiTargets = useHasPermission('kpi', 'set_targets')
  // Attributes is reachable by tags:manage holders (admin + manager) so managers
  // can manage practice tags; the rest of Attributes stays admin-only, filtered
  // inside the forms/fields grids (#903).
  const canManageTags = useHasPermission('tags', 'manage')
  const isAttributesSection = parseResult.success && parseResult.data === 'attributes'
  const canAccessAdminSection = canManageSettings || (isAttributesSection && canManageTags)
  const { isLoading: permissionsLoading } = useUserPermissions()
  const blockedFromAdminSection = isAdminOnlySection && !permissionsLoading && !canAccessAdminSection

  // KPI state — must be at top level even if not used for non-KPI sections
  const [showKPIForm, setShowKPIForm] = useState(false)
  const [year, setYear] = useState(getCurrentYear())
  const [period, setPeriod] = useState<KPIPeriod>('quarterly')

  const { data: ownProgress, isLoading: ownLoading } = useKPIProgress(year, period)
  const { data: teamProgress, isLoading: teamLoading } = useTeamKPIProgress(year, period)

  useEffect(() => {
    if (!parseResult.success) {
      navigate({ to: '/settings', replace: true })
    }
  }, [parseResult.success, navigate])

  useEffect(() => {
    if (blockedFromAdminSection) {
      navigate({ to: '/settings', replace: true })
    }
  }, [blockedFromAdminSection, navigate])

  if (!parseResult.success) return null

  // Don't render an admin section until we've confirmed `settings:manage`
  // (avoids a flash of admin tooling before the redirect lands).
  if (isAdminOnlySection && (permissionsLoading || !canAccessAdminSection)) return null

  const section = parseResult.data

  const yearOptions = getAvailableYears().map(y => ({ value: String(y), label: String(y) }))
  const periodOptions = [
    { value: 'monthly', label: 'Monthly' },
    { value: 'quarterly', label: 'Quarterly' },
  ]

  const handleExport = () => {
    const data = teamProgress && teamProgress.length > 0 ? teamProgress : ownProgress
    if (data && data.length > 0) exportKPIProgress(data, year, period)
  }

  if (section === 'notifications') return <NotificationSettings />
  if (section === 'general') return <GeneralSettings />
  if (section === 'users') return <UserManagement />
  if (section === 'roles') return <RolesAndPermissions />
  if (section === 'attributes') return <DropdownOptions />
  if (section === 'apikeys') return <ApiKeyManagement />
  if (section === 'changelog') return <ChangelogManagement />
  if (section === 'holidays') return <HolidayManagement />
  if (section === 'daytypes') return <DayTypeManagement />
  if (section === 'metrics') return <ScoreCardMetricsConfig />
  if (section === 'maintenance') return <DataMaintenance />
  if (section === 'utilities') return <UtilitiesSettings />

  // section === 'kpi'
  return (
    <div className={styles.contentSection}>
      <div className={styles.panelToolbar}>
        <div className={styles.panelToolbarLeft}>
          <Select
            options={periodOptions}
            value={period}
            onChange={e => setPeriod(e.target.value as KPIPeriod)}
          />
          <Select
            options={yearOptions}
            value={String(year)}
            onChange={e => setYear(Number(e.target.value))}
          />
        </div>
        <div className={styles.panelToolbarRight}>
          <Button
            variant="secondary"
            size="sm"
            onClick={handleExport}
            leftIcon={
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" strokeLinecap="round" strokeLinejoin="round" />
                <polyline points="7,10 12,15 17,10" strokeLinecap="round" strokeLinejoin="round" />
                <line x1="12" y1="15" x2="12" y2="3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            }
          >
            Export
          </Button>
          {canSetKpiTargets && (
            <Button size="sm" onClick={() => setShowKPIForm(!showKPIForm)}>
              {showKPIForm ? 'Cancel' : 'Set Target'}
            </Button>
          )}
        </div>
      </div>

      {canSetKpiTargets && showKPIForm && (
        <Card className={styles.formCard}>
          <CardHeader title="Set KPI Target" />
          <CardBody>
            <KPIForm onSuccess={() => setShowKPIForm(false)} />
          </CardBody>
        </Card>
      )}

      <div className={styles.contentBlock}>
        <h4 className={styles.contentBlockTitle}>My Progress</h4>
        <KPIProgress
          data={ownProgress || []}
          isLoading={ownLoading}
          emptyMessage="No KPI targets set for you"
        />
      </div>

      <div className={styles.contentBlock}>
        <h4 className={styles.contentBlockTitle}>Team Progress</h4>
        <KPIProgress
          data={teamProgress || []}
          isLoading={teamLoading}
          emptyMessage="No KPI targets set for your team"
        />
      </div>
    </div>
  )
}
